import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import {
  ArrowLeft,
  RefreshCw,
  RotateCcw,
  Send,
  Loader,
  ArrowUpRight,
  Check,
  AlertCircle,
  Copy,
  Download,
} from "lucide-react";
import * as api from "../lib/api";
import { useCmsConnected } from "../components/CmsGate";

const PRIMARY = "#018D4C";

function SaveBadge({ status }) {
  if (status === "saving")
    return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400">
        <Loader size={10} className="animate-spin" /> Saving…
      </span>
    );
  if (status === "saved")
    return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400">
        <Check size={10} /> Saved
      </span>
    );
  if (status === "error")
    return (
      <span className="flex items-center gap-1 text-[10px] text-red-400">
        <AlertCircle size={10} /> Save failed
      </span>
    );
  return (
    <span className="text-[10px] text-zinc-300">Unsaved</span>
  );
}

function EditorPane({ outline, onSave, onStatusChange, editorRef }) {
  const editor = useCreateBlockNote({
    initialContent: outline.blocks?.length > 0 ? outline.blocks : undefined,
  });
  const timer = useRef(null);
  editorRef.current = editor;

  useEffect(() => {
    const unsub = editor.onChange(() => {
      clearTimeout(timer.current);
      onStatusChange("unsaved");
      timer.current = setTimeout(() => onSave(editor.document), 1500);
    });
    return () => {
      unsub?.();
      clearTimeout(timer.current);
    };
  }, [editor, onSave, onStatusChange]);

  return (
    <div className="max-w-3xl mx-auto py-8 px-2">
      <BlockNoteView editor={editor} theme="light" />
    </div>
  );
}

export default function OutlineEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [outline, setOutline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [actionLoading, setActionLoading] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [wpStatus, setWpStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const editorRef = useRef(null);
  // GSC-first: without a CMS, the draft leaves as Markdown (copy / download) instead of a push.
  const cmsConnected = useCmsConnected();

  useEffect(() => {
    api
      .getOutline(id)
      .then((data) => {
        setOutline(data);
        if (data.wp_draft_id) {
          api.getOutlineWpStatus(id).then((s) => setWpStatus(s.status || null)).catch(() => {});
        }
      })
      .catch(() => navigate("/dashboard/writing"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSave = useCallback(
    async (blocks) => {
      setSaveStatus("saving");
      try {
        const updated = await api.updateOutline(id, { blocks });
        setOutline((prev) => ({ ...prev, ...updated }));
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      }
    },
    [id]
  );

  const handleRegenerate = async () => {
    if (!confirm("Regenerate this outline? Current edits will be replaced.")) return;
    setActionLoading("regenerate");
    try {
      const updated = await api.regenerateOutline(id);
      setOutline(updated);
      setEditorKey((k) => k + 1);
      setSaveStatus("saved");
    } catch (e) {
      alert("Regeneration failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevert = async () => {
    if (!confirm("Revert to the original generated outline? Edits will be lost.")) return;
    setActionLoading("revert");
    try {
      const updated = await api.revertOutline(id);
      setOutline(updated);
      setEditorKey((k) => k + 1);
      setSaveStatus("saved");
    } catch (e) {
      alert("Revert failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePushToWp = async () => {
    setActionLoading("wp");
    try {
      const result = await api.pushOutlineToWp(id);
      if (result.success) {
        setOutline((prev) => ({
          ...prev,
          wp_draft_link: result.link,
          wp_draft_edit_link: result.edit_link,
          wp_pushed_at: new Date().toISOString(),
        }));
        setWpStatus("draft");
      } else {
        alert("Push failed: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      alert("Push failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setActionLoading(null);
    }
  };

  const toMarkdown = async () => {
    const editor = editorRef.current;
    const body = editor ? await editor.blocksToMarkdownLossy(editor.document) : "";
    return outline?.title ? `# ${outline.title}\n\n${body}` : body;
  };

  const handleCopyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(await toMarkdown());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("[outline] copy failed:", e);
    }
  };

  const handleDownloadMarkdown = async () => {
    const blob = new Blob([await toMarkdown()], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(outline?.title || "draft").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "draft"}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader size={20} className="animate-spin text-zinc-300" />
      </div>
    );
  }
  if (!outline) return null;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => navigate("/dashboard/writing")}
            className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-700 transition-colors flex-shrink-0"
          >
            <ArrowLeft size={15} />
          </button>
          <div className="min-w-0">
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-400">
              Draft
              {outline.cluster ? ` · ${outline.cluster}` : ""}
            </p>
            <h1 className="text-sm font-bold text-zinc-900 truncate max-w-xs sm:max-w-sm">
              {outline.title || "Untitled"}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <SaveBadge status={saveStatus} />

          <div className="w-px h-4 bg-zinc-200 mx-1" />

          <button
            onClick={handleRevert}
            disabled={!!actionLoading}
            title="Revert to original"
            className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-700 transition-colors disabled:opacity-40"
          >
            {actionLoading === "revert" ? (
              <Loader size={14} className="animate-spin" />
            ) : (
              <RotateCcw size={14} />
            )}
          </button>

          <button
            onClick={handleRegenerate}
            disabled={!!actionLoading}
            title="Regenerate outline"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-40"
          >
            {actionLoading === "regenerate" ? (
              <Loader size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            <span className="hidden sm:inline">Regenerate</span>
          </button>

          {wpStatus && (
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                wpStatus === "publish"
                  ? "bg-green-100 text-green-700"
                  : "bg-zinc-100 text-zinc-500"
              }`}
            >
              {wpStatus === "publish" ? "Published" : wpStatus === "draft" ? "Draft" : wpStatus}
            </span>
          )}

          <button
            onClick={handleCopyMarkdown}
            title="Copy as Markdown"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
          </button>

          <button
            onClick={handleDownloadMarkdown}
            title="Download as Markdown (.md)"
            className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <Download size={14} />
          </button>

          <button
            onClick={handlePushToWp}
            disabled={!!actionLoading || cmsConnected === false}
            title={cmsConnected === false ? "Connect a CMS in Settings to push drafts. Copy or download the Markdown meanwhile." : undefined}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-40"
            style={{ backgroundColor: PRIMARY }}
          >
            {actionLoading === "wp" ? (
              <Loader size={12} className="animate-spin" />
            ) : (
              <Send size={12} />
            )}
            <span className="hidden sm:inline">
              {outline.wp_draft_link
                ? wpStatus === "publish"
                  ? "Update Post"
                  : "Update Draft"
                : "Push to Site"}
            </span>
          </button>

          {outline.wp_draft_edit_link && (
            <a
              href={outline.wp_draft_edit_link}
              target="_blank"
              rel="noopener noreferrer"
              title="Open draft"
              className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-700 transition-colors"
            >
              <ArrowUpRight size={14} />
            </a>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-auto">
        <EditorPane
          key={editorKey}
          outline={outline}
          onSave={handleSave}
          onStatusChange={setSaveStatus}
          editorRef={editorRef}
        />
      </div>
    </div>
  );
}
